import clsx from "clsx";
import { ChangeEvent, type FC, useState } from "react";

import { GroupConfigLabel } from "../GroupConfigLabel";
import { usePageGroupOperation } from "../use-page-group-operation";

import { CancelSaveMenu } from "components/menus/CancelSaveMenu";
import { t } from "i18n/i18n";
import { TextField } from "mds/components/TextField";
import { Tooltip } from "mds/components/Tooltip";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ChevronDownIcon, PersonSingleIcon } from "mds/icons";
import { storeApi } from "store/index";
import { pageSelectors } from "store/selectors";
import { useObjectSelector } from "store/store-hooks";

interface GroupSizeConfigProps {
  pageId: string;
  fullWidth?: boolean;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  title: string;
}
export const GroupSizeConfig: FC<GroupSizeConfigProps> = ({
  pageId,
  fullWidth = false,
  isDisabled,
  isReadOnly,
  title,
}) => {
  const { isPending, runPageGroupOperation } = usePageGroupOperation(pageId);
  const controlsDisabled = isDisabled || isPending;
  const page = useObjectSelector((s) => pageSelectors.selectById(s, pageId), ["max_group_size"]);
  const [currentGroupSize, setCurrentGroupSize] = useState(page?.max_group_size ?? null);
  const [isGroupSizeInvalid, setIsGroupSizeInvalid] = useState(false);

  const onMaxGroupSizeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    const newMaxGroupSize = input === "" ? null : parseInt(input, 10);
    if (newMaxGroupSize === null || (!isNaN(newMaxGroupSize) && newMaxGroupSize >= 2)) {
      setIsGroupSizeInvalid(false);
      setCurrentGroupSize(newMaxGroupSize);
    } else {
      setCurrentGroupSize(newMaxGroupSize);
      setIsGroupSizeInvalid(true);
    }
  };

  if (!page) {
    return null;
  }

  return (
    <Tooltip content={controlsDisabled || isReadOnly ? title : ""}>
      <CancelSaveMenu
        button={
          <SquareButton
            className={clsx(fullWidth && "w-full justify-between")}
            disabled={controlsDisabled || isReadOnly}
            kind="ghost"
            size="s"
            title={!controlsDisabled && !isReadOnly ? title : ""}
          >
            <GroupConfigLabel
              full={t("group_configuration_card.group_size.size_limit", {
                value: page.max_group_size ?? t("group_configuration_card.group_size.no_limit"),
              })}
              short={t("group_configuration_card.group_size.size_limit_short", {
                value: page.max_group_size ?? t("group_configuration_card.group_size.no_limit"),
              })}
            />
            {!isReadOnly && <ChevronDownIcon className={clsx(fullWidth && "ml-auto")} />}
          </SquareButton>
        }
        isSaveDisabled={isGroupSizeInvalid || controlsDisabled || isReadOnly}
        saveEventName="Group settings - Group sizing updated"
        saveEventProps={{ fieldValue: currentGroupSize ?? undefined }}
        title={t("group_configuration_card.config.size")}
        onCancel={() => setCurrentGroupSize(page.max_group_size)}
        onOpen={() => {
          setCurrentGroupSize(page.max_group_size);
          setIsGroupSizeInvalid(page.max_group_size !== null && page.max_group_size < 2);
        }}
        onSave={() => {
          if (controlsDisabled || isReadOnly) return;
          runPageGroupOperation(() =>
            storeApi.pages.partial_update({ id: pageId, max_group_size: currentGroupSize }),
          ).catch(() => {}); // Store requests already report errors.
        }}
      >
        <div className="flex w-full flex-col">
          <TextField
            iconStart={<PersonSingleIcon />}
            min="2"
            placeholder={t("group_configuration_card.group_size.no_limit")}
            size="s"
            type="number"
            value={currentGroupSize === null ? "" : currentGroupSize.toString()}
            onChange={onMaxGroupSizeChange}
          />

          <div
            className={clsx(
              "body-xs mt-1 flex w-full",
              isGroupSizeInvalid ? "text-red" : "text-black-tint-70",
            )}
          >
            {isGroupSizeInvalid
              ? t("group_configuration_card.group_size.error")
              : currentGroupSize === null
                ? t("group_configuration_card.group_size.no_size_limit")
                : t("group_configuration_card.group_size.limited_to", { count: currentGroupSize })}
          </div>
        </div>
      </CancelSaveMenu>
    </Tooltip>
  );
};
